import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Form } from 'react-final-form';
import { Button, Typography, helperMargin, Box, FinalFormInput } from '@utex/ui-kit';
import { createValidator, min, required, max } from '@unitedtraders/validators';
import { GroupPanel } from '@utex/ui-kit/desktop';
import { Loader } from '../../../../../../components/Loader';
import { Layout } from '../../../../../../components/Layout';
import { useI18nService } from '../../../../../../hooks/useI18nService';
import { buttonDownload, container, field } from './styles';
import { ModalContext, Modals } from '../../../../../../modalsContext';
import { ContentProps } from '../../../../types';
import { useBankDetails } from './hooks/useBankDetails';
import { useSubmit } from './hooks/useSubmit';
import { Details } from './components/Details';
import { Instruction } from './components/Instruction';

export interface FormValues {
  amount: number;
}

export const USDOnlineForm: React.FC<ContentProps> = ({ parameters }) => {
  const { t } = useTranslation('byBank');
  const i18nService = useI18nService();
  const { onSubmit } = useSubmit('USD');
  const { details } = useBankDetails();
  const { setCurrentModalOpen } = useContext(ModalContext);

  const validate = createValidator({
    amount: [required(), min(parameters.minimum), max(parameters.maximum)],
  });

  if (!details)
    return (
      <Layout.FormContent>
        <Loader />
      </Layout.FormContent>
    );

  return (
    <Layout.FormContent>
      {i18nService.language === 'ru' && (
        <Box marginBottom={15}>
          <Instruction />
        </Box>
      )}

      <Typography.Paragraph size="M" weight="bold" css={helperMargin({ bottom: 10 })}>
        {t('Otherwise paste these details into the SWIFT transfer form in your Internet bank')}
      </Typography.Paragraph>

      <Details {...details} />

      <Typography.Paragraph size="M" weight="bold" css={helperMargin({ bottom: 10 })}>
        {t('Some banks require a receipt and contract')}
      </Typography.Paragraph>

      <Form<FormValues>
        onSubmit={onSubmit}
        validate={validate}
        render={(form) => (
          <form onSubmit={form.handleSubmit} noValidate>
            <GroupPanel>
              <FinalFormInput
                name="amount"
                id="amount"
                type="number"
                inputMode="decimal"
                allowedSymbols={/\d|\.|\,/g}
                label="$"
                isLabelShort
                placeholder="0"
                view="small"
                hintView="error"
                hintPosition="right"
                styles={{
                  container,
                  field,
                }}
              />
              <Button
                type="submit"
                view="primary-dim"
                css={buttonDownload}
                isLoading={form.submitting}
              >
                {t('Download')}
              </Button>
            </GroupPanel>
          </form>
        )}
      />

      <Box>
        <Button
          onClick={() => setCurrentModalOpen(Modals.DONE)}
          view="regular"
          css={helperMargin({ top: 20 })}
        >
          {t('Have paid')}
        </Button>
      </Box>
    </Layout.FormContent>
  );
};
